// common/utils/date.utils.ts
// This file contains utility functions for date manipulation
"use strict";

import * as moment from "moment-timezone";

/**
 * Extend Moment interface with custom methods
 */
declare module "moment" {
    interface Moment {
        /**
         * Formats date for database storage (YYYY-MM-DD)
         * @returns Formatted date string
         */
        toDatabaseDate(): string;
        /**
         * Formats datetime for database storage (YYYY-MM-DD HH:mm:ss)
         * @returns Formatted datetime string
         */
        toDatabaseDateTime(): string;
    }
}

// Implement the custom functions
moment.fn.toDatabaseDate = function (): string {
    return this.format("YYYY-MM-DD");
};

moment.fn.toDatabaseDateTime = function (): string {
    return this.format("YYYY-MM-DD HH:mm:ss");
};

/**
 * Creates a moment object in Central Standard Time (CST - America/Chicago)
 *
 * @param args - Arguments to pass to moment constructor
 * @returns A moment object set to CST timezone
 */
export const cstMoment = (...args: any[]): moment.Moment => moment(...args).tz("America/Chicago");

/**
 * Creates a moment object in UTC timezone
 *
 * @param args - Arguments to pass to moment constructor
 * @returns A moment object set to UTC timezone
 */
export const utcMoment = (...args: any[]): moment.Moment => moment(...args).tz("UTC");

/**
 * Gets the current week number of the year
 * @returns Current week number
 */
export const CURRENT_WEEK = cstMoment().week();

/**
 * Checks if the current day is Monday
 *
 * @returns True if today is Monday, false otherwise
 */
export const isMonday = cstMoment().weekday() === 1;

/**
 * Returns true if the current day is Friday (evaluated at call time in CST).
 * Implemented as a function rather than a module-level constant to avoid
 * stale day-of-week values in long-running processes.
 *
 * @returns True if today is Friday, false otherwise
 */
export const isFriday = (): boolean => cstMoment().weekday() === 5;

/**
 * Parses a date string to start-of-day in CST timezone
 *
 * @param dateString - Date string (YYYY-MM-DD or YYYY-MM-DD HH:mm:ss)
 * @returns Date object at start of day if date-only, or parsed timestamp in CST
 */
export const parseToStartOfDayCST = (dateString: string): Date => {
    return dateString.includes(":") ? cstMoment(dateString).toDate() : cstMoment(`${dateString} 00:00:00`).toDate();
};

/**
 * Parses a date string to end-of-day in CST timezone
 *
 * @param dateString - Date string (YYYY-MM-DD or YYYY-MM-DD HH:mm:ss)
 * @returns Date object at end of day (23:59:59.999) if date-only, or parsed timestamp in CST
 */
export const parseToEndOfDayCST = (dateString: string): Date => {
    return dateString.includes(":") ? cstMoment(dateString).toDate() : cstMoment(`${dateString} 23:59:59.999`).toDate();
};

/**
 * Gets start and end dates of the week containing the given date
 *
 * @param date - Date to get week boundaries for
 * @returns Object with startOfWeek and endOfWeek dates
 */
export const getStartAndEndOfWeek = (date: string): { startOfWeek: string; endOfWeek: string } => {
    const dateMoment = cstMoment(date);
    const startOfWeek = dateMoment.clone().startOf("isoWeek").toDatabaseDate(); // Monday
    const endOfWeek = dateMoment.clone().endOf("isoWeek").toDatabaseDate(); // Sunday

    return { startOfWeek, endOfWeek };
};
